import { useEffect, useCallback, useMemo, Dispatch } from 'react';
import { useFlashTransition } from '__hooks/useFlashState';
import { VariableErrorTypes } from '../VariableWizardContext';
import { StaticValidationKeys } from './ValidationKeys';

export const useErrors = (
  id: string,
  errors: VariableErrorTypes,
  setErrors: Dispatch<VariableErrorTypes>
) => {
  const [message, showMessage, flashErrorMessage, cancelFlash] =
    useFlashTransition();

  const variableError = (errors || {})[id];

  const hasError = Boolean(
    variableError && Object.values(variableError).length
  );

  // find the first error
  const errorKey = 'value';

  useEffect(() => {
    if (errorKey && variableError && variableError[errorKey]) {
      flashErrorMessage(variableError[errorKey]);
    }
  }, [variableError, flashErrorMessage, errorKey]);

  const setError = useCallback(
    <T extends string = StaticValidationKeys>(
      prop: T,
      errorMessage: string[]
    ) => {
      setErrors({ ...errors, [id]: { ...errors[id], [prop]: errorMessage } });
    },
    [errors, id, setErrors]
  );

  const validate = useMemo(
    () => ({
      showMessage,
      message,
      popperConfig: {
        modifiers: {
          preventOverflow: { enabled: false },
        },
      },
    }),
    [showMessage, message]
  );

  const clearError = useCallback(
    (prop: StaticValidationKeys | string) => {
      if (hasError) {
        if (showMessage && errorKey === prop) {
          cancelFlash();
        }

        const { [id]: _, ...nextErrors } = errors;

        setErrors(nextErrors);
      }
    },
    [cancelFlash, errors, hasError, id, setErrors, showMessage]
  );

  return { setError, errors, validate, clearError };
};
